// Convex service for handling Convex-specific operations
import { useMutation, useQuery } from "convex/react";
import { api } from "../../backend/_generated/api";
import { Id } from "../../backend/_generated/dataModel";

export const useConvexAuth = () => {
  const login = useMutation(api.auth.login);
  const registerInvestor = useMutation(api.auth.registerInvestor);
  const registerProjectHolder = useMutation(api.auth.registerProjectHolder);
  const registerAdmin = useMutation(api.auth.registerAdmin);
  const getUser = useMutation(api.auth.getUser);
  const updateUserStatus = useMutation(api.auth.updateUserStatus);
  const updateUserDetails = useMutation(api.auth.updateUserDetails);
  const deleteUser = useMutation(api.auth.deleteUser);
  const getUsersByStatus = (status: "pending_approval" | "approved" | "rejected" | "suspended") =>
    useQuery(api.auth.getUsersByStatus, { status });
  const getUsersByRole = (role: "investor" | "project_holder" | "admin") =>
    useQuery(api.auth.getUsersByRole, { role });

  return {
    login,
    registerInvestor,
    registerProjectHolder,
    registerAdmin,
    getUser,
    updateUserStatus,
    updateUserDetails,
    deleteUser,
    getUsersByStatus,
    getUsersByRole
  };
};

export const useConvexProjects = () => {
  const createProject = useMutation(api.projects.createProject);
  const getAllProjects = useQuery(api.projects.getAllProjects);
  const getProjectsByOwner = (ownerId: Id<"users">) =>
    useQuery(api.projects.getProjectsByOwner, { ownerId });
  const getProjectById = (projectId: Id<"projects">) =>
    useQuery(api.projects.getProjectById, { projectId });
  const updateProjectStatus = useMutation(api.projects.updateProjectStatus);
  const getProjectsByStatus = (status: "draft" | "pending_approval" | "approved" | "funded" | "completed") =>
    useQuery(api.projects.getProjectsByStatus, { status });
  const getProjectsByCategory = (category: string) =>
    useQuery(api.projects.getProjectsByCategory, { category });
  const getProjectsForInvestors = () =>
    useQuery(api.projects.getProjectsForInvestors);
  const updateProject = useMutation(api.projects.updateProject);
  const deleteProject = useMutation(api.projects.deleteProject);

  return {
    createProject,
    getAllProjects,
    getProjectsByOwner,
    getProjectById,
    updateProjectStatus,
    getProjectsByStatus,
    getProjectsByCategory,
    getProjectsForInvestors,
    updateProject,
    deleteProject
  };
};

export const useConvexInvestments = () => {
  const createInvestment = useMutation(api.investments.createInvestment);
  const getInvestmentsByProject = (projectId: Id<"projects">) =>
    useQuery(api.investments.getInvestmentsByProject, { projectId });
  const getInvestmentsByInvestor = (investorId: Id<"users">) =>
    useQuery(api.investments.getInvestmentsByInvestor, { investorId });
  const getInvestmentsByProjectOwner = (ownerId: Id<"users">) =>
    useQuery(api.investments.getInvestmentsByProjectOwner, { ownerId });
  const getInvestmentById = (investmentId: Id<"investments">) =>
    useQuery(api.investments.getInvestmentById, { investmentId });
  const refundInvestment = useMutation(api.investments.refundInvestment);
  const approveInvestmentByProjectHolder = useMutation(api.investments.approveInvestmentByProjectHolder);
  const approveInvestmentByAdmin = useMutation(api.investments.approveInvestmentByAdmin);
  const rejectInvestment = useMutation(api.investments.rejectInvestment);
  const getInvestmentsByStatus = (status: "pending" | "project_holder_approved" | "admin_approved" | "confirmed" | "refunded" | "rejected") =>
    useQuery(api.investments.getInvestmentsByStatus, { status });

  return {
    createInvestment,
    getInvestmentsByProject,
    getInvestmentsByInvestor,
    getInvestmentsByProjectOwner,
    getInvestmentById,
    refundInvestment,
    approveInvestmentByProjectHolder,
    approveInvestmentByAdmin,
    rejectInvestment,
    getInvestmentsByStatus
  };
};

export const useConvexNotifications = () => {
  const createNotification = useMutation(api.notifications.createNotification);
  const getUnreadNotifications = (userId: Id<"users">) =>
    useQuery(api.notifications.getUnreadNotifications, { userId });
  const getAllNotifications = (userId: Id<"users">) =>
    useQuery(api.notifications.getAllNotifications, { userId });
  const markAsRead = useMutation(api.notifications.markAsRead);
  const markAllAsRead = useMutation(api.notifications.markAllAsRead);
  const deleteNotification = useMutation(api.notifications.deleteNotification);
  const deleteReadNotifications = useMutation(api.notifications.deleteReadNotifications);

  return {
    createNotification,
    getUnreadNotifications,
    getAllNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications
  };
};

export const useConvexProjectSales = () => {
  const createProjectSale = useMutation(api.projectSales.createProjectSale);
  const getProjectSaleById = (projectSaleId: Id<"project_sales">) =>
    useQuery(api.projectSales.getProjectSaleById, { projectSaleId });
  const getProjectSalesByProject = (projectId: Id<"projects">) =>
    useQuery(api.projectSales.getProjectSalesByProject, { projectId });
  const getProjectSalesBySeller = (sellerId: Id<"users">) =>
    useQuery(api.projectSales.getProjectSalesBySeller, { sellerId });
  const getProjectSalesByStatus = (status: "pending" | "accepted" | "completed" | "cancelled") =>
    useQuery(api.projectSales.getProjectSalesByStatus, { status });
  const acceptProjectSale = useMutation(api.projectSales.acceptProjectSale);
  const completeProjectSale = useMutation(api.projectSales.completeProjectSale);
  const cancelProjectSale = useMutation(api.projectSales.cancelProjectSale);
  const getAllProjectSales = () =>
    useQuery(api.projectSales.getAllProjectSales);

  return {
    createProjectSale,
    getProjectSaleById,
    getProjectSalesByProject,
    getProjectSalesBySeller,
    getProjectSalesByStatus,
    acceptProjectSale,
    completeProjectSale,
    cancelProjectSale,
    getAllProjectSales
  };
};

export const useConvexFiles = () => {
  const storeFileMetadata = useMutation(api.files.storeFileMetadata);
  const getFileById = (fileId: Id<"files">) =>
    useQuery(api.files.getFileById, { fileId });
  const getFilesByProject = (projectId: Id<"projects">, fileType?: "image" | "document") =>
    useQuery(api.files.getFilesByProject, { projectId, fileType });
  const deleteFileMetadata = useMutation(api.files.deleteFileMetadata);

  return {
    storeFileMetadata,
    getFileById,
    getFilesByProject,
    deleteFileMetadata
  };
};

export const useConvexDataMigration = () => {
  const migrateExistingProjects = useMutation(api.initData.migrateExistingProjects);
  const migrateProjectsWithPurpose = useMutation(api.initData.migrateProjectsWithPurpose);
  const updateAllProjectsWithPurpose = useMutation(api.initData.updateAllProjectsWithPurpose);
  const updateProjectsWithSalesToSellPurpose = useMutation(api.initData.updateProjectsWithSalesToSellPurpose);

  return {
    migrateExistingProjects,
    migrateProjectsWithPurpose,
    updateAllProjectsWithPurpose,
    updateProjectsWithSalesToSellPurpose
  };
};

export const useConvexBanners = () => {
  const getBanners = () => useQuery(api.banners.getBanners);
  const getAllBanners = () => useQuery(api.banners.getAllBanners);
  const createBanner = useMutation(api.banners.createBanner);
  const updateBanner = useMutation(api.banners.updateBanner);
  const deleteBanner = useMutation(api.banners.deleteBanner);

  return {
    getBanners,
    getAllBanners,
    createBanner,
    updateBanner,
    deleteBanner,
  };
};